import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import {
  getSelectedOptionsFromHorizonForm,
  resolveConfiguratorVariantIds,
} from './configurator-utils.js';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  calcConfiguratorPrice = () => {
    const variantPickerForm = document.querySelector('form.variant-picker__form');
    if (!variantPickerForm) return '';

    const selected = getSelectedOptionsFromHorizonForm(variantPickerForm);

    const boardColor = selected["Board Color"] || selected["Polclap színe"];
    const connectorColor = selected["Connector Color"] || selected["Összekötő elem színe"];

    const {
      boardSizeSId,
      boardSizeMId,
      boardSizeLId,
      legId,
      screwId,
      connectorId
    } = resolveConfiguratorVariantIds({ boardColor, connectorColor });

    if (!boardSizeSId || !boardSizeMId || !boardSizeLId || !legId || !screwId || !connectorId) {
      return '';
    }

    const parts = /** @type {any} */ (window).shelfParts;
    //const parts = /** @type {any} */ (window).ShelfConfigurator.parts; // TODO: bring window.shelfParts under window.ShelfConfigurator.parts
    const configuratorPartsVariants = /** @type {any} */ (window).ShelfConfigurator.variants;
    if (!parts || !configuratorPartsVariants) return '';

    const boardsSizeSQuantity = parts.boards[0];
    const boardsSizeMQuantity = parts.boards[1];
    const boardsSizeLQuantity = parts.boards[2];
    const legsQuantity = parts.legs;
    const totalBoards = parts.boards[0] + parts.boards[1] + parts.boards[2];
    const screwsQuantity = totalBoards * 4;
    const connectorsQuantity = parts.cuboids;

    const boardSizeSPrice = configuratorPartsVariants.boards_size_s[String(boardSizeSId)]?.price ?? 0;
    const boardSizeMPrice = configuratorPartsVariants.boards_size_m[String(boardSizeMId)]?.price ?? 0;
    const boardSizeLPrice = configuratorPartsVariants.boards_size_l[String(boardSizeLId)]?.price ?? 0;
    const legPrice = configuratorPartsVariants.legs[String(legId)]?.price ?? 0;
    const screwPrice = configuratorPartsVariants.screws[String(screwId)]?.price ?? 0;
    const connectorPrice = configuratorPartsVariants.connectors[String(connectorId)]?.price ?? 0;

    const totalPrice =
      (boardSizeSPrice * boardsSizeSQuantity) +
      (boardSizeMPrice * boardsSizeMQuantity) +
      (boardSizeLPrice * boardsSizeLQuantity) +
      (legPrice * legsQuantity) +
      (screwPrice * screwsQuantity) +
      (connectorPrice * connectorsQuantity);

    if (Shopify.currency.active === 'EUR') {
      return '€' + (totalPrice / 100.0).toFixed(2);
    }
    return (totalPrice / 100.0).toFixed(2) + 'Ft';
  };

  updateConfiguratorPrice = () => {
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (!currentPrice) return;
    const new_price = this.calcConfiguratorPrice();
    return currentPrice.innerHTML = new_price;
  };

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    const product_form_components = document.querySelectorAll('product-form-component[data-configurator="true"]');
    if(product_form_components.length == 1) {
      return this.updateConfiguratorPrice();
    }

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    } 
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
